// Derives a Tailwind-style 50-900 shade scale from a single brand hex color,
// by keeping hue/saturation fixed and mapping each shade to a target
// lightness (mirroring how the app's default `primary` palette is spread).
const SHADE_LIGHTNESS: Record<string, number> = {
  '50': 97,
  '100': 94,
  '200': 86,
  '300': 74,
  '400': 60,
  '500': 48,
  '600': 39,
  '700': 32,
  '800': 27,
  '900': 22,
}

export const BRAND_SHADE_KEYS = Object.keys(SHADE_LIGHTNESS)

interface HSL {
  h: number
  s: number
  l: number
}

function hexToHsl(hex: string): HSL | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const int = parseInt(match[1], 16)
  const r = ((int >> 16) & 255) / 255
  const g = ((int >> 8) & 255) / 255
  const b = (int & 255) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const delta = max - min

  let h = 0
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    switch (max) {
      case r:
        h = ((g - b) / delta) % 6
        break
      case g:
        h = (b - r) / delta + 2
        break
      default:
        h = (r - g) / delta + 4
    }
    h *= 60
    if (h < 0) h += 360
  }

  return { h, s: s * 100, l: l * 100 }
}

function hslToHex(h: number, s: number, l: number): string {
  const sFrac = s / 100
  const lFrac = l / 100
  const c = (1 - Math.abs(2 * lFrac - 1)) * sFrac
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = lFrac - c / 2

  let r = 0
  let g = 0
  let b = 0
  if (h < 60) {
    r = c; g = x; b = 0
  } else if (h < 120) {
    r = x; g = c; b = 0
  } else if (h < 180) {
    r = 0; g = c; b = x
  } else if (h < 240) {
    r = 0; g = x; b = c
  } else if (h < 300) {
    r = x; g = 0; b = c
  } else {
    r = c; g = 0; b = x
  }

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/** Generates a { "50": "#...", ..., "900": "#..." } shade map from one hex color. */
export function generateShades(baseHex: string): Record<string, string> | null {
  const hsl = hexToHsl(baseHex)
  if (!hsl) return null

  const shades: Record<string, string> = {}
  for (const [shade, lightness] of Object.entries(SHADE_LIGHTNESS)) {
    shades[shade] = hslToHex(hsl.h, hsl.s, lightness)
  }
  return shades
}
